import {
    MdEuro,
    MdOutlinePersonPin,
    MdOutlineShoppingBasket,
    MdPallet,
    MdTrendingUp,
    MdTrendingDown,
} from 'react-icons/md';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import { useDisclosure } from '@chakra-ui/hooks';
import { GiCargoCrate } from 'react-icons/gi';
import { TbMoneybag, TbPigMoney } from 'react-icons/tb';
import { BsCalendar3 } from 'react-icons/bs';

import StockValue from './components/ProjectStatus';
import Card from 'components/card';
import { getDashboardAnalytics, getUsersLight, getMyInfo } from 'interfaces/user';
import { getStockAlerts } from 'interfaces/stock';
import { getTodayTasks } from 'interfaces/task';
import AverageBasket from './components/averageBasket';
import SleepingCustomers from 'views/admin/main/account/billing/components/sleepingCustomers';
import OrdersLines from './components/OrdersLines';
import InputField from 'components/fields/InputField';
import BenefitLines from './components/BenefitLines';
import VolumeTable from './components/volumeTable';
import { useShepherdTour } from 'react-shepherd';

function getToday() {
    return new Date().toISOString().split('T')[0];
}

function getSevenDaysAgo() {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
}

const steps: any = [
    {
        id: 'intro',
        attachTo: { element: '.first-element', on: 'bottom' },
        beforeShowPromise: function () {
            return new Promise<void>(function (resolve) {
                setTimeout(function () {
                    window.scrollTo(0, 0);
                    resolve();
                }, 500);
            });
        },
        buttons: [
            {
                classes: 'shepherd-button-secondary',
                text: 'Pas maintenant',
                type: 'cancel',
            },
            {
                classes: 'shepherd-button-primary',
                text: 'Commencer',
                type: 'next',
            },
        ],
        classes: 'custom-class-name-1 custom-class-name-2',
        highlightClass: 'highlight',
        scrollTo: false,
        cancelIcon: {
            enabled: true,
        },
        title: 'Se lancer avec Toola 🚀',
        text: [
            'Dans 10 minutes vos premiers clients pourront déjà passer commande automatiquement !',
        ],
        when: {
            show: () => {
                console.log('show step');
            },
            hide: () => {
                console.log('hide step');
            },
        },
    },
    {
        title: '1/5: Paramétrer votre compte',
        text: 'Vous pouvez à tout moment modifier vos paramètres via ce menu déroulant',
        attachTo: {
            element: '.step-user-menu',
            on: 'left',
        },
        buttons: [
            {
                classes: 'shepherd-button-primary',
                text: 'Paramétrer mon compte',
                action() {
                    window.location.href = '/settings';
                },
            },
        ],
        id: 'step-user-menu',
    },
];

const Dashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<any>(null);
    const [startDate, setStartDate] = useState<any>(getSevenDaysAgo());
    const [endDate, setEndDate] = useState<any>(getToday());
    const [allDates, setAllDates] = useState<any>([]);
    const [alerts, setAlerts] = useState<any>([]);
    const [todayTasks, setTodayTasks] = useState<any>([]);
    const [alertModalOpened, setAlertModalOpened] = useState<boolean>(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [users, setUsers] = useState<any[]>([]);
    const [assignedTo, setAssignedTo] = useState<string>('');
    const [quickDateRange, setQuickDateRange] = useState<string>('7d');
    
    const tourOptions = {
        defaultStepOptions: {
            cancelIcon: {
                enabled: true,
            },
        },
        useModalOverlay: true,
    };
    const tour = useShepherdTour({ tourOptions, steps });

    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const ownerId = localStorage.getItem('ownerId'); // If exists, it's a sub-account
    const currentUserId = localStorage.getItem('userId');


    // Logic of permissions:
    // 1. No ownerId = main owner → See everything
    // 2. ownerId exists + "all" or "show:number-dashboard" permission → See everything
    // 3. ownerId exists + no permission → See only their assigned clients
    
    const isOwner = !ownerId; // No ownerId = main owner
    const hasFullPermission = permissions.includes('all') || permissions.includes('show:number-dashboard');
    
    // Without full permission, a sub-account can only see their own data
    const isRestrictedUser = ownerId && !hasFullPermission;
    const canSeeDashboard = true; // Everyone can see the dashboard

    const initDashboardData = async (startDate: string, endDate: string, assignedTo?: string) => {

        const res = await getDashboardAnalytics(startDate, endDate, assignedTo);
        
        if (res.error) {
            console.error("Erreur dashboard:", res.error);
            return;
        }
        
        setData(res.data);
    };

    const initStockAlerts = async () => {
        const res = await getStockAlerts();

        if (res.error) {
            window.location.href = '/auth/login';
            return;
        }
        setAlerts(res.data);
        localStorage.setItem('alertsNbr', res.data.length);
    };

    const initTodayTasks = async () => {
        const res = await getTodayTasks();
        if (res) {
            setTodayTasks(res);
            localStorage.setItem('todayTasksNbr', res.length.toString());
        }
    };

    const handleQuickDateRange = (range: string) => {
        setQuickDateRange(range);
        const today = new Date();
        const start = new Date();
        
        switch (range) {
            case '7d':
                start.setDate(today.getDate() - 7);
                break;
            case '30d':
                start.setDate(today.getDate() - 30);
                break;
            case '90d':
                start.setDate(today.getDate() - 90);
                break;
            case 'month':
                start.setDate(1);
                break;
            case 'year':
                start.setMonth(0, 1);
                break;
            default:
                start.setDate(today.getDate() - 7);
        }
        
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
    };

    const AlertModal = (props: { isOpen: any; onClose: any }) => {
        const { isOpen, onClose } = props;
        return (
            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay className="bg-[#000] !opacity-30" />
                <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] top-[12vh]">
                    <ModalBody>
                        <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[450px] flex flex-col !z-[1004]">
                            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100">
                                <span className="text-3xl">⚠️</span>
                            </div>
                            <h1 className="mb-4 text-center text-2xl font-bold text-navy-700">
                                Alertes détectées
                            </h1>
                            <div className="space-y-3 mb-6">
                                {alerts.filter((alert: any) => alert.type === 'quantity-alert').length > 0 && (
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50">
                                        <span className="text-red-500 text-xl flex-shrink-0">📦</span>
                                        <div>
                                            <p className="font-medium text-navy-700">Seuils d'alerte atteints</p>
                                            <p className="text-sm text-gray-600">
                                                {alerts.filter((a: any) => a.type === 'quantity-alert').length} produit(s) concerné(s)
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {alerts.filter((alert: any) => alert.type === 'dlc-alert').length > 0 && (
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50">
                                        <span className="text-red-500 text-xl flex-shrink-0">🔴</span>
                                        <div>
                                            <p className="font-medium text-navy-700">DLC dépassées</p>
                                            <p className="text-sm text-gray-600">
                                                {alerts.filter((a: any) => a.type === 'dlc-alert').length} produit(s) périmé(s)
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {alerts.filter((alert: any) => alert.type === 'dlc-warning').length > 0 && (
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-50">
                                        <span className="text-orange-500 text-xl flex-shrink-0">⏰</span>
                                        <div>
                                            <p className="font-medium text-navy-700">DLC proches</p>
                                            <p className="text-sm text-gray-600">
                                                {alerts.filter((a: any) => a.type === 'dlc-warning').length} produit(s) à surveiller
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {alerts.filter((alert: any) => alert.type === 'delivery-issue').length > 0 && (
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-50">
                                        <span className="text-orange-500 text-xl flex-shrink-0">🚚</span>
                                        <div>
                                            <p className="font-medium text-navy-700">Problèmes de livraison</p>
                                            <p className="text-sm text-gray-600">
                                                {alerts.filter((a: any) => a.type === 'delivery-issue').length} incident(s) signalé(s)
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {todayTasks.length > 0 && (
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 cursor-pointer" onClick={() => navigate('/tasks')}>
                                        <span className="text-blue-500 text-xl flex-shrink-0">✅</span>
                                        <div>
                                            <p className="font-medium text-navy-700">Tâches du jour</p>
                                            <p className="text-sm text-gray-600">
                                                {todayTasks.length} tâche{todayTasks.length > 1 ? 's' : ''} à effectuer aujourd'hui
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={onClose}
                                    className="flex-1 rounded-xl border-2 border-gray-300 px-5 py-3 text-base font-medium text-gray-700 transition duration-200 hover:bg-gray-50"
                                >
                                    Fermer
                                </button>
                                <button
                                    className="flex-1 rounded-xl bg-[#0260CB] px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-[#01499b] active:bg-[#024086]"
                                    onClick={() => navigate('/alerts')}
                                >
                                    Voir tout ({alerts.length})
                                </button>
                            </div>
                        </Card>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    };

    useEffect(() => {
        // The onboarding tour is only for the main owner
        if (data && data.productsNbr === 0 && isOwner) {
            tour.start();
            localStorage.setItem('onboarding-step', '1');
        }
    }, [data]);

    useEffect(() => {
        if (data && (alerts.length > 0 || todayTasks.length > 0) && !alertModalOpened) {
            const delayDebounceFn = setTimeout(() => {
                onOpen();
                setAlertModalOpened(true);
                return () => clearTimeout(delayDebounceFn);
            }, 500);
        }
    }, [data, alerts, todayTasks]);

    useEffect(() => {
        if (localStorage.getItem('token') === null) navigate('/auth/login');
        
        // Retrieve and store the userId if necessary
        const ensureUserId = async () => {
            if (!currentUserId) {
                const userInfo = await getMyInfo();
                if (userInfo.data && userInfo.data._id) {
                    localStorage.setItem('userId', userInfo.data._id);
                    console.log('✅ userId stocké:', userInfo.data._id);
                    // Forced reload to apply new settings
                    window.location.reload();
                    return;
                }
            }
            
            // If user is restricted, always filter by their own ID
            const initialAssignedTo = isRestrictedUser ? currentUserId : (assignedTo || undefined);
            if (isRestrictedUser && currentUserId) {
                setAssignedTo(currentUserId);
            }
            
            initDashboardData(startDate, endDate, initialAssignedTo);
            initStockAlerts();
            initTodayTasks();
            (async () => {
                const u = await getUsersLight();
                if (u) setUsers(u);
            })();
        };
        
        ensureUserId();
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            const dates = [];
            const start = new Date(startDate);
            const end = new Date(endDate);
            while (start <= end) {
                dates.push(new Date(start));
                start.setDate(start.getDate() + 1);
            }

            setAllDates(dates);
            // If user is restricted, always filter by their own ID
            const effectiveAssignedTo = isRestrictedUser ? currentUserId : (assignedTo || undefined);
            initDashboardData(startDate, endDate, effectiveAssignedTo);
        }, 300);
        return () => clearTimeout(delayDebounceFn);
    }, [startDate, endDate, assignedTo]);

    const calculateEvolution = (current: number, previous: number) => {
        if (!previous) return 0;
        return ((current - previous) / previous) * 100;
    };

    return (
        <div className="mt-3 h-full w-full">
            <AlertModal isOpen={isOpen} onClose={onClose} />
            
            {/* Header with filters */}
            <Card extra="mb-5 p-4">
            <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
                {/* Shortcuts period */}
                <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
                    {[
                        { label: '7j', value: '7d' },
                        { label: '30j', value: '30d' },
                        { label: '90j', value: '90d' },
                        { label: 'Mois', value: 'month' },
                        { label: 'Année', value: 'year' },
                    ].map((range) => (
                        <button
                            key={range.value}
                            onClick={() => handleQuickDateRange(range.value)}
                            className={`flex-shrink-0 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                                quickDateRange === range.value
                                    ? 'bg-[#0260CB] text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {range.label}
                        </button>
                    ))}
                </div>

                <div className="hidden lg:block h-8 w-px bg-gray-200"></div>

                {/* date selector */}
                <div className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-white lg:flex-1 lg:max-w-md">
                    <BsCalendar3 className="text-gray-400 flex-shrink-0" />
                    <InputField
                        placeholder="Début"
                        id="startDate"
                        type="date"
                        value={startDate}
                        onChange={(e: any) => {
                            setQuickDateRange('custom');
                            setStartDate(e.target.value);
                        }}
                        inputSmall={true}
                        extra="!mb-0 !border-0 !w-auto"
                    />
                    <span className="text-gray-400 flex-shrink-0">→</span>
                    <InputField
                        placeholder="Fin"
                        id="endDate"
                        type="date"
                        value={endDate}
                        onChange={(e: any) => {
                            setQuickDateRange('custom');
                            setEndDate(e.target.value);
                        }}
                        inputSmall={true}
                        extra="!mb-0 !border-0 !w-auto"
                    />
                </div>

                {/* Filter by salesperson - Only visible for users with full permissions */}
                {users.length > 0 && !isRestrictedUser && (
                    <div className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-white lg:min-w-[220px]">
                        <select
                            value={assignedTo}
                            onChange={(e) => setAssignedTo(e.target.value)}
                            className="flex-1 text-sm outline-none bg-transparent"
                        >
                            <option value="">Tous les commerciaux</option>
                            {users.map((u) => (
                                <option key={u._id} value={u._id}>
                                    {u.fullName}
                                </option>
                            ))}
                        </select>
                        {assignedTo && (
                            <button
                                onClick={() => setAssignedTo('')}
                                className="text-gray-400 hover:text-gray-600 flex-shrink-0 text-xl leading-none"
                            >
                                ×
                            </button>
                        )}
                    </div>
                )}
                
                {/* Indicator for restricted users */}
                {isRestrictedUser && (
                    <div className="flex items-center gap-2 p-2 rounded-lg border border-blue-200 bg-blue-50 lg:min-w-[220px]">
                        <span className="text-sm text-blue-700 font-medium">
                            📊 Mes statistiques personnelles
                        </span>
                    </div>
                )}
            </div>
            {/* Dashboard V2 - user without restrictions */}
            {!isRestrictedUser && (
                <button
                    onClick={() => navigate('/dashboard/v2')}
                    className="text-xs sm:text-sm text-toola-400 hover:text-toola-500 hover:underline flex items-center gap-1 transition-colors"
                >
                    Découvrir le nouveau dashboard
                    <span className="text-xs">→</span>
                </button>
            )}
        </Card>

            {/* KPIs */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4 mb-5">
                <div id="step-one">
                    <Card extra="p-5 hover:shadow-xl transition-shadow">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm text-gray-600 mb-1">CA du jour</p>
                                <h3 className="text-2xl font-bold text-navy-700">
                                    {canSeeDashboard && data ? `${data.todayBenefit} €` : '-'}
                                </h3>
                                {canSeeDashboard && data && data.draftTodayBenefit > 0 && (
                                    <p className="text-xs text-orange-500 mt-1 font-medium">
                                        +{Math.round(data.draftTodayBenefit)} € en brouillon
                                    </p>
                                )}
                                {data && data.yesterdayBenefit && (
                                    <div className="flex items-center gap-1 mt-2">
                                        {data.todayBenefit > data.yesterdayBenefit ? (
                                            <MdTrendingUp className="text-green-500" />
                                        ) : (
                                            <MdTrendingDown className="text-red-500" />
                                        )}
                                        <span className={`text-xs ${
                                            data.todayBenefit > data.yesterdayBenefit 
                                                ? 'text-green-500' 
                                                : 'text-red-500'
                                        }`}>
                                            {Math.abs(calculateEvolution(data.todayBenefit, data.yesterdayBenefit)).toFixed(1)}%
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary">
                                <MdOutlineShoppingBasket className="h-7 w-7 text-[#0260CB]" />
                            </div>
                        </div>
                    </Card>
                </div>

                <Card extra="p-5 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <p className="text-sm text-gray-600 mb-1">CA de la période</p>
                            <h3 className="text-2xl font-bold text-navy-700">
                                {canSeeDashboard && data ? `${data.benefitPerPeriod} €` : '-'}
                            </h3>
                            {canSeeDashboard && data && data.draftBenefitPerPeriod > 0 && (
                                <p className="text-xs text-orange-500 mt-1 font-medium">
                                    +{Math.round(data.draftBenefitPerPeriod)} € en brouillon
                                </p>
                            )}
                        </div>
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary">
                            <MdEuro className="h-7 w-7 text-[#0260CB]" />
                        </div>
                    </div>
                </Card>

                <Card extra="p-5 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <p className="text-sm text-gray-600 mb-1">Volume</p>
                            <h3 className="text-2xl font-bold text-navy-700">
                                {canSeeDashboard && data ? `${data.volumePerPeriod} Kg` : '-'}
                            </h3>
                        </div>
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary">
                            <TbMoneybag className="h-7 w-7 text-[#0260CB]" />
                        </div>
                    </div>
                </Card>

                <Card extra="p-5 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <p className="text-sm text-gray-600 mb-1">Commandes</p>
                            <h3 className="text-2xl font-bold text-navy-700">
                                {canSeeDashboard && data ? `${data.totalOrdersPerPeriod}` : '-'}
                            </h3>
                        </div>
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary">
                            <TbPigMoney className="h-7 w-7 text-[#0260CB]" />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Charts and Analytics */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3 mb-5">
                <BenefitLines
                    data={data?.sparkLinesBenefit}
                    allDates={allDates}
                    canSeeDashboard={canSeeDashboard}
                />
                <OrdersLines
                    data={data?.sparklinesOrders}
                    allDates={allDates}
                />
                <AverageBasket
                    data={data?.sparklinesBasket}
                    allDates={allDates}
                />
            </div>

            {/* Customers and analytics */}
            {/* <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-5">
                <TopCustomers data={data?.topCustomers} />
                <TopCustomersMap data={data?.topCustomers} />
            </div> */}

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3 mb-5">
                <SleepingCustomers data={data?.inactiveCustomers} />
                <StockValue data={data} />
                {/* <StockAlert data={data} /> */}
            </div>

            {/* Volume table and estimates */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-5">
                <VolumeTable data={data?.volumeTable} />
                {/* <LosingEstimation data={data} /> */}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-5">
                <Card extra="p-5 hover:shadow-xl transition-all cursor-pointer group" onClick={() => navigate('/create-order')}>
                    <div className="text-center">
                        <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-blue-50 group-hover:bg-blue-100 transition-colors mb-4">
                            <MdPallet className="h-8 w-8 text-[#0260CB]" />
                        </div>
                        <h4 className="font-bold text-navy-700 mb-1">Nouvelle commande</h4>
                        <p className="text-sm text-gray-600">Créer une commande rapidement</p>
                    </div>
                </Card>

                <Card extra="p-5 hover:shadow-xl transition-all cursor-pointer group" onClick={() => navigate('/create-customer')}>
                    <div className="text-center">
                        <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-green-50 group-hover:bg-green-100 transition-colors mb-4">
                            <MdOutlinePersonPin className="h-8 w-8 text-green-600" />
                        </div>
                        <h4 className="font-bold text-navy-700 mb-1">Nouveau client</h4>
                        <p className="text-sm text-gray-600">Ajouter un client à votre base</p>
                    </div>
                </Card>

                <Card extra="p-5 hover:shadow-xl transition-all cursor-pointer group" onClick={() => navigate('/delivery/delivery-list?f=1')}>
                    <div className="text-center">
                        <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-purple-50 group-hover:bg-purple-100 transition-colors mb-4">
                            <GiCargoCrate className="h-8 w-8 text-purple-600" />
                        </div>
                        <h4 className="font-bold text-navy-700 mb-1">Livraisons</h4>
                        <p className="text-sm text-gray-600">Gérer vos tournées du jour</p>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default Dashboard;